import Mustache from 'mustache';
import { Descendant, Text } from 'slate';
import {
  ConceptElement,
  EmoticonElement,
  DividingLineElement,
  ImageElement,
  ParagraphElement,
  ParamTextElement,
  ParamHolderElement,
  CustomText,
} from '../slateTypes';
import { CustomSlateNodeType, ImageInfo, PackageType } from '../types';
import serializerMap from './serializer';
import textSerializer from './serializer/textSerializer';
import { getRegionLanguageMap } from 'hooks/business/system/useRegionLanguage';

const paramCanEditLabels = ['店铺地址', '商品地址'];

export function mustache2SlateValue(mustache: string): Descendant[] {
  const imageNodes: ImageInfo[] = [];
  const result = Mustache.parse(mustache).reduce(
    (article, item) => {
      if (item[0] === 'text') {
        return article;
      }
      let content = '';
      const tag = item[1].split('.');
      const type = tag[0];
      if (item[4] && item[4][0]) {
        content = item[4][0][1] || '';
      }

      try {
        content = decodeURIComponent(content || '');
      } catch (e) {
        // 老旧数据未经过encodeURIComponent处理时，若包含特殊符号(如: %)会导致decode失败,对于此类可以直接continue到后续输出.
      }

      switch (type) {
        case CustomSlateNodeType.TEXT:
          article[article.length - 1].push({ text: content || '' });
          break;
        case CustomSlateNodeType.PARAMTEXT:
          article[article.length - 1].push({
            type,
            label: content,
            canEditor: !(content && paramCanEditLabels.includes(content)),
            children: [{ text: '' }],
          } as ParamTextElement);
          break;
        case CustomSlateNodeType.PARAMHOLDER:
          article[article.length - 1].push({
            type,
            label: content,
            children: [{ text: '' }],
          } as ParamHolderElement);
          break;
        case CustomSlateNodeType.CONCEPT: {
          article[article.length - 1].push({
            type,
            label: content,
            children: [{ text: '' }],
          } as ConceptElement);
          break;
        }
        case CustomSlateNodeType.IMAGE:
          imageNodes.push({
            imageId: content as string,
          });
          break;
        case CustomSlateNodeType.DIVIDING_LINE:
          article.push(
            [
              {
                type,
                children: [{ text: '' }],
              } as DividingLineElement,
            ],
            [],
          );
          break;
        default:
          break;
      }
      return article;
    },
    [[]] as Descendant[][],
  );

  let exceptImageNodes = result.reduce((final, block) => {
    if (block.length === 1 && block[0].type === CustomSlateNodeType.DIVIDING_LINE) {
      final.push(block[0]);
    } else if (block.length) {
      final.push({
        type: CustomSlateNodeType.PARAGRAPH,
        children: block,
      } as ParagraphElement);
    }
    return final;
  }, [] as Descendant[]);

  // 如果解析出来没有数据,则给一个空paragraph节点(slate的有效默认值)
  if (!exceptImageNodes.length) {
    exceptImageNodes = [
      {
        type: CustomSlateNodeType.PARAGRAPH,
        children: [{ text: '' }],
      },
    ];
  }

  return imageNodes.length
    ? [
        ...exceptImageNodes,
        {
          type: CustomSlateNodeType.IMAGE,
          images: imageNodes,
          children: [{ text: '' }],
        } as ImageElement,
      ]
    : exceptImageNodes;
}

export function mustache2SlateValueWithCatch(mustache: string) {
  try {
    return mustache2SlateValue(mustache);
  } catch (err) {
    return undefined;
  }
}

/** ai策略发送内容的类型:`表情,图片,视频,邀请关注`等数据增加前处理 */
function lastEmptyArrPop(arr: any[][]) {
  if (arr.length && !arr[arr.length - 1].length) {
    arr.pop();
  }
}

/** 适配 AI策略 发送内容支持多种消息类型 */
export function mustache2SlateValuePolicyMsgTypes(mustache: string): Descendant[] {
  const result = Mustache.parse(mustache).reduce(
    (article, item) => {
      if (item[0] === 'text') {
        return article;
      }
      let content = '';
      const tag = item[1].split('.');
      const type = tag[0];
      if (item[4] && item[4][0]) {
        content = item[4][0][1] || '';
      }

      try {
        content = decodeURIComponent(content || '');
      } catch (e) {
        // 老旧数据未经过encodeURIComponent处理时，若包含特殊符号(如: %)会导致decode失败,对于此类可以直接continue到后续输出.
      }

      switch (type) {
        case CustomSlateNodeType.TEXT:
          article[article.length - 1].push({ text: content || '' });
          break;
        case CustomSlateNodeType.EMOTICON:
          article[article.length - 1].push({
            type,
            shortcut: content,
            packageName: tag[1] as PackageType,
            children: [{ text: '' }],
          } as EmoticonElement);
          break;
        case CustomSlateNodeType.PARAMTEXT:
          article[article.length - 1].push({
            type,
            label: content,
            canEditor: !(content && paramCanEditLabels.includes(content)),
            children: [{ text: '' }],
          } as ParamTextElement);
          break;
        case CustomSlateNodeType.PARAMHOLDER:
          article[article.length - 1].push({
            type,
            label: content,
            children: [{ text: '' }],
          } as ParamHolderElement);
          break;
        case CustomSlateNodeType.CONCEPT: {
          article[article.length - 1].push({
            type,
            label: content,
            children: [{ text: '' }],
          } as ConceptElement);
          break;
        }
        case CustomSlateNodeType.STICKER:
          lastEmptyArrPop(article);
          article.push(
            [
              {
                type: CustomSlateNodeType.IMAGE,
                images: content
                  ? [
                      {
                        imageId: content as string,
                      },
                    ]
                  : [],
                children: [{ text: CustomSlateNodeType.STICKER }],
              } as ImageElement,
            ],
            [],
          );
          break;
        case CustomSlateNodeType.MEDIA:
          const media = (() => {
            try {
              return JSON.parse(content);
            } catch (_) {
              return undefined;
            }
          })();

          if (media && media.mediaType === 'MEDIA_IMAGE') {
            lastEmptyArrPop(article);

            article.push(
              [
                {
                  type: CustomSlateNodeType.IMAGE,
                  images: content
                    ? [
                        {
                          imageId: media.coverUrl || media.localUrl,
                          media: content,
                        },
                      ]
                    : [],
                  children: [
                    {
                      text: CustomSlateNodeType.IMAGE,
                    },
                  ],
                },
              ],
              [],
            );
          }

          if (media && media.mediaType === 'MEDIA_VIDEO') {
            lastEmptyArrPop(article);

            article.push(
              [
                {
                  type: CustomSlateNodeType.IMAGE,
                  images: content
                    ? [
                        {
                          imageId: media.coverUrl || media.localUrl,
                          media: content,
                        },
                      ]
                    : [],
                  children: [
                    {
                      text: CustomSlateNodeType.VIDEO,
                    },
                  ],
                },
              ],
              [],
            );
          }

          break;
        case CustomSlateNodeType.IMAGE:
          lastEmptyArrPop(article);
          article.push(
            [
              {
                type: CustomSlateNodeType.IMAGE,
                images: content
                  ? [
                      {
                        imageId: content as string,
                      },
                    ]
                  : [],
                children: [{ text: CustomSlateNodeType.IMAGE }],
              } as ImageElement,
            ],
            [],
          );
          break;
        case CustomSlateNodeType.VIDEO:
          lastEmptyArrPop(article);
          article.push(
            [
              {
                type: CustomSlateNodeType.IMAGE,
                images: content
                  ? [
                      {
                        imageId: content as string,
                      },
                    ]
                  : [],
                children: [{ text: CustomSlateNodeType.VIDEO }],
              } as ImageElement,
            ],
            [],
          );
          break;
        case CustomSlateNodeType.FOLLOW:
          lastEmptyArrPop(article);
          article.push(
            [
              {
                type: CustomSlateNodeType.IMAGE,
                images: content
                  ? [
                      {
                        imageId: content as string,
                      },
                    ]
                  : [],
                children: [{ text: CustomSlateNodeType.FOLLOW }],
              } as ImageElement,
            ],
            [],
          );
          break;
        case CustomSlateNodeType.QRCODE:
          lastEmptyArrPop(article);
          article.push(
            [
              {
                type: CustomSlateNodeType.IMAGE,
                images: [],
                children: [{ text: CustomSlateNodeType.QRCODE }],
              } as ImageElement,
            ],
            [],
          );
          break;
        case CustomSlateNodeType.DIVIDING_LINE:
          article.push(
            [
              {
                type,
                children: [{ text: '' }],
              } as DividingLineElement,
            ],
            [],
          );
          break;
        case CustomSlateNodeType.ITEM:
          article.push(
            [
              {
                type: CustomSlateNodeType.ITEM,
              },
            ],
            [],
          );
          break;
        case CustomSlateNodeType.VOUCHER:
          article.push(
            [
              {
                type: CustomSlateNodeType.VOUCHER,
              },
            ],
            [],
          );
          break;
        case CustomSlateNodeType.FILE: {
          const { resourceId, fileName, type, localUrl } = JSON.parse(content);
          article.push(
            [
              {
                type: CustomSlateNodeType.FILE,
                data: {
                  filename: resourceId,
                  originalFilename: fileName,
                  type,
                  size: '',
                  url: localUrl,
                },
              },
            ],
            [],
          );
          break;
        }
        case CustomSlateNodeType.LOGISTICS: {
          article.push(
            [
              {
                type: CustomSlateNodeType.LOGISTICS,
                preText: content,
              },
            ],
            [],
          );
          break;
        }
        default:
          break;
      }
      return article;
    },
    [[]] as Descendant[][],
  );

  let allNodes = result.reduce((final, block) => {
    if (block.length === 1 && block[0].type === CustomSlateNodeType.DIVIDING_LINE) {
      final.push(block[0]);
    } else if (block.length) {
      if (!block[0].type || block[0].type === CustomSlateNodeType.CONCEPT) {
        if (block[0].type === CustomSlateNodeType.CONCEPT) {
          block.unshift({ text: '' });
        }
        if (block[block.length - 1].type === CustomSlateNodeType.CONCEPT) {
          block.push({ text: '' });
        }
        final.push({
          type: CustomSlateNodeType.PARAGRAPH,
          children: block,
        } as ParagraphElement);
      } else {
        final.push(block[0]);
      }
    }
    return final;
  }, [] as Descendant[]);

  // 如果解析出来没有数据,则给一个空paragraph节点(slate的有效默认值)
  if (!allNodes.length) {
    allNodes = [
      {
        type: CustomSlateNodeType.PARAGRAPH,
        children: [{ text: '' }],
      },
    ];
  }

  return allNodes;
}

export type MsgType =
  | 'text'
  | 'sticker'
  | 'image'
  | 'video'
  | 'follow'
  | 'qrcode'
  | 'item'
  | 'logistics'
  | 'voucher';

export function getMsgOfNoData(type: MsgType, region?: General.UpperCaseRegion) {
  const emptyData = {
    text: {
      type: 'paragraph',
      children: [{ text: '' }],
    },
    sticker: {
      type: 'image',
      images: [],
      children: [
        {
          text: 'sticker',
        },
      ],
    },
    image: {
      type: 'image',
      images: [],
      children: [
        {
          text: 'image',
        },
      ],
    },
    video: {
      type: 'image',
      images: [],
      children: [
        {
          text: 'video',
        },
      ],
    },
    follow: {
      type: 'image',
      images: [],
      children: [
        {
          text: 'follow',
        },
      ],
    },
    qrcode: {
      type: 'image',
      images: [],
      children: [
        {
          text: 'qrcode',
        },
      ],
    },
    item: {
      type: 'item',
    },
    logistics: {
      type: 'logistics',
      preText:
        (
          {
            'zh-CN': '后续如果您想要持续跟踪物流可以点击查看物流卡片：',
            id: 'Selanjutnya, jika Anda ingin terus memantau pengiriman, silakan klik untuk melihat kartu logistik:',
            th: 'หลังจากนี้ถ้าคุณต้องการติดตามสถานะขนส่งอย่างต่อเนื่องสามารถคลิกดูการ์ดข้อมูลการขนส่งได้:',
            'en-US': 'If you wish to follow up on the shipment, you can click the logistics card:',
            vi: 'Sau này, nếu bạn muốn theo dõi liên tục tình trạng vận chuyển, có thể nhấp để xem thẻ vận chuyển:',
            pt: 'Se desejar continuar rastreando a logística, clique para olhar o cartão de logística:',
            'zh-TW': '後續如果您想要持續跟蹤物流可以點擊查看物流卡片：',
            tl: 'ung gusto mong mag-follow up sa shipment, puwede mong i-click yung logistics card:',
            pl: 'Jeśli chcesz dalej śledzić przesyłkę, kliknij, aby wyświetlić kartę przesyłki:',
            es: 'Si desea hacer un seguimiento del envío, puede hacer clic en la tarjeta de logística:',
            ar: 'Si desea hacer un seguimiento del envío, puede hacer clic en la tarjeta de logística:',
            my: 'Selepas ini, jika anda ingin terus menjejak status penghantaran, anda boleh klik untuk melihat kad logistik:',
            ms: 'Selepas ini, jika anda ingin terus menjejak status penghantaran, anda boleh klik untuk melihat kad logistik:',
            de: 'Falls Sie den Versand weiterverfolgen möchten, klicken Sie auf die Versandkarte, um sie anzusehen:',
            it: 'Se desideri continuare a tracciare la spedizione, puoi fare clic per visualizzare la scheda di spedizione:',
            ja: 'もし引き続き配送状況を追跡されたい場合は、配送カードをクリックしてご覧ください：',
            ie: 'If you wish to follow up on the shipment, you can click the logistics card:',
            fr: 'Si vous souhaitez continuer à suivre votre livraison, vous pouvez cliquer pour voir la fiche de suivi:',
          } as any
        )[(getRegionLanguageMap() as any)[region as any]] ||
        'If you wish to follow up on the shipment, you can click the logistics card:',
    },
    voucher: {
      type: 'voucher',
    },
  };
  return emptyData[type] as Descendant;
}

export function SlateValue2Mustache(value: Descendant[], isTemplateMustache?: boolean): string {
  return value
    .reduce<any[]>((dest, node) => {
      const { type } = node;
      if (!isTemplateMustache && type === CustomSlateNodeType.PARAMHOLDER) {
        return [...dest, textSerializer({ text: (node as ParamHolderElement).label })];
      }
      if (Text.isText(node)) {
        if (node.text.trim()) {
          return [...dest, textSerializer(node)];
        }
        return dest;
      }
      if (type === CustomSlateNodeType.PARAGRAPH) {
        return [...dest, SlateValue2Mustache(node.children, isTemplateMustache)];
      }
      return [...dest, serializerMap[type as string](node)];
    }, [])
    .join('');
}

export function SlateValue2MustacheV2(value: Descendant[], isTemplateMustache?: boolean): string {
  return value
    .reduce<any[]>((dest, node) => {
      const { type } = node;
      if (!isTemplateMustache && type === CustomSlateNodeType.PARAMHOLDER) {
        return [...dest, textSerializer({ text: (node as ParamHolderElement).label })];
      }
      if (Text.isText(node)) {
        if (node.text.trim()) {
          return [...dest, textSerializer(node)];
        }
        return dest;
      }
      if (type === CustomSlateNodeType.PARAGRAPH) {
        return [...dest, SlateValue2Mustache(node.children, isTemplateMustache)];
      }
      if (type === 'image') {
        const actualType = (node.children[0] as CustomText).text;
        if (actualType === CustomSlateNodeType.QRCODE) {
          return [...dest, '{{qrcode}}'];
        }
        if (actualType === CustomSlateNodeType.FOLLOW) {
          return [...dest, '{{follow}}'];
        }
        return [
          ...dest,
          serializerMap[type as string](node)
            .replace('{{#image}}', `{{#${actualType}}}`)
            .replace('{{/image}}', `{{/${actualType}}}`),
        ];
      }
      return [...dest, serializerMap[type as string](node)];
    }, [])
    .join('');
}
